import { useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Pagination, theme, Typography } from 'antd';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { KpiCard } from '../../dashboard/components/KpiCard';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { clientesKeys } from '../../clientes/api/keys';
import { CobroCreateModal } from './CobroCreateModal';
import { CobroDetailModal } from './CobroDetailModal';
import { AnularCobroModal } from './AnularCobroModal';
import { FacturasTableToolbar } from './FacturasTableToolbar';
import { ReemitirCobroModal } from './ReemitirCobroModal';
import { createFacturasTableColumns } from './FacturasTableColumns';
import { useCobrosList, useCobrosStats } from '../api/useCobros';
import { useHasPermission } from '../../../auth/useHasPermission';
import { paginationProps } from '../../../lib/paginacion';
import { useServerPaginacion } from '../../../lib/server-sort';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { useT } from '../../../locales';
import { formatCurrency, Permission } from '@aid/shared';
import type { CobroClienteResponse } from '@aid/shared';
import {
  groupFacturas,
  getFacturaInitials,
  buildFacturasQueryFilters,
} from '../lib/factura-table-helpers';
import {
  EMPTY_FACTURA_TABLE_FILTERS,
  FACTURA_TABLE_GROUP,
  FACTURA_TABLE_SCOPE,
  getFacturasTableCapabilities,
} from './factura-table-types';
import type {
  FacturaTableFilterState,
  FacturaTableGroupBy,
  FacturasTableProps,
} from './factura-table-types';

const { Text } = Typography;

const KPI_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 16,
  marginBottom: 16,
};

export { FACTURA_TABLE_SCOPE } from './factura-table-types';
export type { FacturasTableProps } from './factura-table-types';

export function FacturasTable(props: FacturasTableProps) {
  const t = useT();
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const { scope } = props;
  const capabilities = getFacturasTableCapabilities(scope);
  const fixedClienteId = scope === FACTURA_TABLE_SCOPE.CLIENTE ? props.clienteId : undefined;
  const { page, pageSize, sort, handleChange, setPage } = useServerPaginacion<CobroClienteResponse>(
    capabilities.showPaginationSizeChanger ? 10 : 20,
    capabilities.defaultSort,
  );
  const [filters, setFilters] = useState<FacturaTableFilterState>(EMPTY_FACTURA_TABLE_FILTERS);
  const [groupBy, setGroupBy] = useState<FacturaTableGroupBy>(FACTURA_TABLE_GROUP.NONE);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [anularFactura, setAnularFactura] = useState<CobroClienteResponse | null>(null);
  const [reemitirFactura, setReemitirFactura] = useState<CobroClienteResponse | null>(null);

  const debouncedSearch = useDebouncedValue(filters.search);
  const queryFilters = buildFacturasQueryFilters(
    scope,
    { ...filters, search: debouncedSearch },
    fixedClienteId,
  );
  const { data, isLoading, error, refetch } = useCobrosList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...queryFilters,
    ...sort,
  });

  const { data: stats, isLoading: statsLoading } = useCobrosStats(queryFilters, {
    enabled: capabilities.showStats,
  });
  const { data: estadosCobro } = useCatalogos('ESTADO_COBRO');
  const canCreate = useHasPermission(Permission.COBROS_CREATE);
  const canAnular = useHasPermission(Permission.COBROS_ANULAR);
  const canReissue = useHasPermission(Permission.COBROS_ANULAR);
  const canReadClient = useHasPermission(Permission.CLIENTES_READ);

  const rows = data?.data ?? [];
  const hasFilters =
    (capabilities.showClientFilter && filters.idCliente.trim() !== '') ||
    filters.search.trim() !== '' ||
    filters.tipoFactura !== undefined ||
    filters.idEstado !== undefined ||
    filters.fechaDesde !== undefined ||
    filters.fechaHasta !== undefined;
  const groups = groupFacturas(rows, groupBy, t);
  const caption = hasFilters ? t('common.segunFiltros') : undefined;
  const statsEmpty = statsLoading && !stats;
  const emptyState = (
    <EmptyState
      compact={capabilities.compactEmptyState}
      title={
        capabilities.compactEmptyState
          ? t('pages.clientes.actividad.sinFacturas')
          : t('pages.cobros.emptyTitle')
      }
      description={
        capabilities.compactEmptyState
          ? t('pages.clientes.actividad.sinFacturasDesc')
          : t('pages.cobros.emptyDescription')
      }
    />
  );

  const updateFilters = (next: Partial<FacturaTableFilterState>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FACTURA_TABLE_FILTERS });
    setPage(1);
  };

  const handleInvoiceChanged = () => {
    void refetch();
    if (fixedClienteId === undefined) return;
    void queryClient.invalidateQueries({ queryKey: clientesKeys.detail(fixedClienteId) });
    void queryClient.invalidateQueries({ queryKey: clientesKeys.cobros(fixedClienteId) });
    void queryClient.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(fixedClienteId) });
  };

  // Opens the 3-step ReemitirCobroModal (S13): live preview → reason +
  // confirm with the preview fingerprint → terminal result (close + refresh).
  const onReemitir = (factura: CobroClienteResponse) => {
    setReemitirFactura(factura);
  };

  const onAnular = (factura: CobroClienteResponse) => {
    setAnularFactura(factura);
  };

  const columns = createFacturasTableColumns({
    t,
    showClient: capabilities.showClientColumn,
    groupBy,
    canAnular,
    canReissue,
    canReadClient,
    onDetail: setDetailId,
    onAnular,
    onReemitir,
    primaryColor: token.colorPrimary,
    sort,
  });

  if (error) {
    return (
      <EmptyState compact={capabilities.compactEmptyState} title={t('pages.cobros.errorLoad')} />
    );
  }

  return (
    <div>
      {capabilities.showStats && (
        <div style={{ marginBottom: 16 }}>
          <div data-testid="facturas-kpi-grid" style={KPI_GRID_STYLE}>
            <KpiCard
              label={t('pages.cobros.kpis.emitido')}
              value={stats?.emitido ?? '0'}
              accent="cobros"
              caption={caption}
              empty={statsEmpty}
              compact
            />
            <KpiCard
              label={t('pages.cobros.kpis.pendientes')}
              value={String(stats?.pendientes ?? 0)}
              formatValue={false}
              accent="clientes"
              caption={caption}
              empty={statsEmpty}
              compact
            />
            <KpiCard
              label={t('pages.cobros.kpis.pagadas')}
              value={String(stats?.pagadas ?? 0)}
              formatValue={false}
              accent="pagos"
              caption={caption}
              empty={statsEmpty}
              compact
            />
            <KpiCard
              label={t('pages.cobros.kpis.anuladas')}
              value={String(stats?.anuladas ?? 0)}
              formatValue={false}
              accent="mora"
              caption={caption}
              empty={statsEmpty}
              compact
            />
            <KpiCard
              label={t('pages.cobros.kpis.ticketPromedio')}
              value={stats?.promedio ?? '0'}
              accent="none"
              caption={caption}
              empty={statsEmpty}
              compact
            />
          </div>
        </div>
      )}
      <FacturasTableToolbar
        t={t}
        showClientFilter={capabilities.showClientFilter}
        showGrouping={capabilities.showGrouping}
        showCreate={capabilities.showCreate}
        groupBy={groupBy}
        idCliente={filters.idCliente}
        search={filters.search}
        tipoFactura={filters.tipoFactura}
        idEstado={filters.idEstado}
        fechaDesde={filters.fechaDesde}
        fechaHasta={filters.fechaHasta}
        estadosCobro={estadosCobro ?? []}
        canCreate={canCreate}
        resultTotal={data?.total ?? 0}
        hasFilters={hasFilters}
        onGroupByChange={(value) => {
          setGroupBy(value);
          setPage(1);
        }}
        onClientChange={(value) => updateFilters({ idCliente: value })}
        onSearchChange={(value) => updateFilters({ search: value })}
        onTipoFacturaChange={(value) => updateFilters({ tipoFactura: value })}
        onEstadoChange={(value) => updateFilters({ idEstado: value })}
        onDateRangeChange={(value) =>
          updateFilters({
            fechaDesde: value?.[0] ?? undefined,
            fechaHasta: value?.[1] ?? undefined,
          })
        }
        onClear={clearFilters}
        onCreate={() => setCreateOpen(true)}
      />
      {groups ? (
        groups.length === 0 ? (
          <DataTable<CobroClienteResponse>
            columns={columns}
            dataSource={rows}
            rowKey="id"
            loading={isLoading}
            locale={{ emptyText: emptyState }}
            pagination={false}
            onChange={handleChange}
          />
        ) : (
          <>
            {groups.map((group) => (
              <div key={group.label} style={{ marginBottom: 24 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    marginBottom: 8,
                    borderRadius: 8,
                    background: token.colorFillTertiary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  {groupBy === FACTURA_TABLE_GROUP.CLIENTE && (
                    <Avatar
                      size={24}
                      style={{ background: token.colorPrimary, fontSize: 12 }}
                      aria-hidden
                    >
                      {getFacturaInitials(group.rows[0]?.cliente?.nombre)}
                    </Avatar>
                  )}
                  <Text strong>{group.label}</Text>
                  <Text type="secondary">{group.rows.length}</Text>
                  <Text type="secondary" style={{ marginLeft: 'auto' }}>
                    {formatCurrency(
                      group.rows
                        .reduce((total, factura) => total + Number(factura.valor), 0)
                        .toFixed(2),
                    )}
                  </Text>
                </div>
                <DataTable<CobroClienteResponse>
                  columns={columns}
                  dataSource={group.rows}
                  rowKey="id"
                  pagination={false}
                  onChange={handleChange}
                />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={data?.total ?? 0}
                showSizeChanger={false}
                showTotal={(total, range) =>
                  t('pagination.showTotal', { from: range[0], to: range[1], total })
                }
                onChange={setPage}
              />
            </div>
          </>
        )
      ) : (
        <DataTable<CobroClienteResponse>
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          locale={{ emptyText: emptyState }}
          pagination={paginationProps(t, {
            page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: capabilities.showPaginationSizeChanger,
          })}
          onChange={handleChange}
        />
      )}
      {capabilities.showCreate && (
        <CobroCreateModal
          open={createOpen}
          preselectClienteId={fixedClienteId}
          onClose={() => setCreateOpen(false)}
          onSuccess={handleInvoiceChanged}
        />
      )}
      {detailId !== null && <CobroDetailModal id={detailId} onClose={() => setDetailId(null)} />}
      {anularFactura !== null && (
        <AnularCobroModal
          open
          cobroId={anularFactura.id}
          onClose={() => setAnularFactura(null)}
          onSuccess={handleInvoiceChanged}
        />
      )}
      {reemitirFactura !== null && (
        <ReemitirCobroModal
          open
          cobroId={reemitirFactura.id}
          idCliente={reemitirFactura.idCliente}
          onClose={() => setReemitirFactura(null)}
          onSuccess={handleInvoiceChanged}
        />
      )}
    </div>
  );
}
